// Single axios instance for the Eryndal SPA. Token plumbing lives in the
// `setAuthTokenProvider` injection — Auth0Provider calls it once on startup
// so request interceptors can grab a fresh access token without React-Query
// or Zustand having to know how Auth0 works.

import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
} from "axios";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL ?? "https://api.eryndal.eu";

type TokenProvider = () => Promise<string | null>;

// Synchronous fallback: read the most recent Auth0 access token straight
// out of localStorage. The Auth0 SPA SDK persists tokens with the
// "@@auth0spajs@@::<clientId>::<audience>::<scope>" key format when
// cacheLocation is "localstorage". Reading directly avoids the race
// where the React-side setAuthTokenProvider hook hasn't run yet.
export function readCachedAccessToken(): string | null {
  try {
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith("@@auth0spajs@@") || key.endsWith("@@user@@")) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw) as { body?: { access_token?: string; expiresAt?: number } };
      const token = parsed?.body?.access_token;
      if (!token) continue;
      const expiresAt = parsed?.body?.expiresAt;
      if (expiresAt && expiresAt * 1000 < Date.now()) continue;
      return token;
    }
  } catch {
    // ignore — cache scan is best-effort
  }
  return null;
}

let tokenProvider: TokenProvider = async () => readCachedAccessToken();

export function setAuthTokenProvider(provider: TokenProvider): void {
  tokenProvider = async () => {
    const fromProvider = await provider();
    return fromProvider ?? readCachedAccessToken();
  };
}

function createClient(): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 60_000,
    headers: { "Content-Type": "application/json" },
  });

  client.interceptors.request.use(async (config) => {
    const token = await tokenProvider();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  client.interceptors.response.use(
    (resp) => resp,
    (error: AxiosError) => {
      if (error.response?.status === 401) {
        // Distinguish "we never sent a token" (Auth0 silent refresh
        // failed — recoverable) from "we sent one and the backend
        // rejected it" (server-side, not our problem here).
        // When the token provider returns null, axios doesn't attach
        // the Authorization header — that's the diagnostic signal.
        const hadAuthHeader = Boolean(
          (error.config?.headers as Record<string, string> | undefined)?.Authorization,
        );
        if (!hadAuthHeader) {
          handleStaleAuth();
        }
      }
      return Promise.reject(error);
    },
  );

  return client;
}

// One-shot recovery path for "Missing bearer token" 401s. The SPA can
// fall into this state when Auth0's refresh token expires (or the
// browser blocks third-party cookies), leaving the SDK with a stale
// ``isAuthenticated=true`` but ``getAccessTokenSilently()`` returning
// nothing. Without intervention every API call quietly fails and the
// UI gets stuck.
//
// Recovery: nuke the cached Auth0 entries and reload. On the next
// mount the Auth0 provider sees no token, AuthBridge calls
// ``loginWithRedirect``, the user re-authenticates, and the SPA
// returns to where they were.
//
// Guarded so a misconfigured backend that 401s everything can't pin
// the user in a reload loop.
let _staleAuthRecoveryFired = false;
function handleStaleAuth(): void {
  if (_staleAuthRecoveryFired) return;
  _staleAuthRecoveryFired = true;
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i += 1) {
      const key = localStorage.key(i);
      if (key && key.startsWith("@@auth0spajs@@")) keys.push(key);
    }
    for (const key of keys) localStorage.removeItem(key);
  } catch {
    // localStorage is unavailable in some embedded contexts — best effort.
  }
  // Defer one tick so the rejection still surfaces to the calling
  // useQuery (which renders a friendly retry button) before we reload.
  setTimeout(() => window.location.reload(), 250);
}

export const apiClient = createClient();

export async function apiGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.get<T>(url, config);
  return response.data;
}

export async function apiPost<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.post<T>(url, body, config);
  return response.data;
}

export async function apiDelete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const response = await apiClient.delete<T>(url, config);
  return response.data;
}

export async function apiPatch<T, B = unknown>(
  url: string,
  body?: B,
  config?: AxiosRequestConfig,
): Promise<T> {
  const response = await apiClient.patch<T>(url, body, config);
  return response.data;
}

export const API_BASE = API_BASE_URL;
